import React, { FC, ReactNode } from "react";

import { basketActiveAtom } from "@keepeek/commons";
import { LayoutOverrideKey } from "@keepeek/refront-components";
import { Box, ButtonBase, Grid, useMediaQuery } from "@mui/material";
import { styled, Theme } from "@mui/material/styles";
import { SxProps } from "@mui/system";
import { animated, Spring } from "react-spring";
import { useRecoilState, useRecoilValue } from "recoil";

import { KIcon } from "../../components/common/KIcon";
import SkipLinks from "../../components/common/SkipLinks";
import { CONFIGURATION_SECTION_PATH } from "../../components/layout/adminMenu";
import { getCustomerOverrideComponent } from "../../lib/overrides";
import { Basket } from "../../models/configuration/components/basket";
import { useConfiguration } from "../../providers/config/hooks";
import { frontEditState } from "../../providers/frontEdit/atoms/frontEdit";
import { pdfViewerFullScreenState } from "../../providers/viewer/atoms";
import BasketContainer from "../Basket";
import ContactSheets from "../ContactSheets";
import DefaultAppBar from "../DefaultAppBar";
import DownloadManager from "../DownloadManager";
import EditionModal from "../FrontEdit/EditionModal";
import ImportElement from "../ImportElement";
import ShareManager from "../ShareManager";
import ThumbnailGeneratorModal from "../ThumbnailGeneratorModal";
import { kHeaderWidgetAllBurgerOpentState } from "../widgets/KHeaderWidget/atoms";

const PREFIX = "index";

const classes = {
  container: `${PREFIX}-container`,
  basketBackwall: `${PREFIX}-basketBackwall`,
  basket: `${PREFIX}-basket`,
  closeBasketButton: `${PREFIX}-closeBasketButton`,
  content: `${PREFIX}-content`,
};

const StyledDefaultLayout = styled("div")(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: "grid",
    gridTemplateAreas: `"content basket"`,
    gridTemplateColumns: "minmax(0, 1fr) auto",
    height: "100vh",
  },

  [`& .${classes.basketBackwall}`]: {
    position: "fixed",
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
    backgroundColor: "rgba(0,0,0,0.75)",
    backdropFilter: "blur(5px)",
  },

  [`& .${classes.basket}`]: {
    gridArea: "basket",
    height: "100%",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      zIndex: theme.zIndex.drawer,
      display: "flex",
      alignItems: "flex-start",
    },
  },

  [`& .${classes.closeBasketButton}`]: {
    position: "relative",
    zIndex: theme.zIndex.drawer,
    color: "white",
    padding: theme.spacing(2.5),
  },

  [`& .${classes.content}`]: {
    gridArea: "content",
  },
}));

const AnimatedBox = animated(Box);

export type DefaultLayoutProps = {
  children: ReactNode;
  sx?: SxProps<Theme>;
};

const DefaultLayout: FC<React.PropsWithChildren<DefaultLayoutProps>> = function ({ children, sx }) {
  const { displayBaskets } = useConfiguration<Basket>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_BASKET,
  ) ?? { displayBaskets: undefined };
  // Responsive basket
  const [activeBasket, setActiveBasket] = useRecoilState(basketActiveAtom);
  const isDownSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const frontEdit = useRecoilValue(frontEditState);

  // PDF Preview
  const pdfIsFullScreen = useRecoilValue(pdfViewerFullScreenState);

  // Responsive menu
  const burgerOpen = useRecoilValue(kHeaderWidgetAllBurgerOpentState);
  return (
    <>
      <StyledDefaultLayout
        data-testid="overflowControl"
        id="overflowControl"
        className={classes.container}
        sx={sx}
        style={{
          maxHeight:
            (activeBasket && isDownSm) || pdfIsFullScreen || !frontEdit || burgerOpen
              ? "100vh"
              : "inherit",
          overflow:
            (activeBasket && isDownSm) || pdfIsFullScreen || burgerOpen ? "hidden" : "inherit",
          height: frontEdit ? "unset" : "initial",
        }}
      >
        <Grid className={classes.content} container>
          <Grid item xs={12}>
            <Box
              component="main"
              role="main"
              id="main"
              style={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                height: "100%",
              }}
            >
              <SkipLinks />
              <DefaultAppBar hasBasket={displayBaskets === undefined || displayBaskets} />
              {children}
            </Box>
          </Grid>
        </Grid>
        {(displayBaskets === undefined || displayBaskets) && (
          <Box className={classes.basket}>
            {activeBasket && isDownSm && (
              <>
                <Spring to={{ opacity: 1 }} from={{ opacity: 0 }}>
                  {(styles) => (
                    <AnimatedBox
                      onClick={() => setActiveBasket(false)}
                      className={classes.basketBackwall}
                      style={styles}
                    />
                  )}
                </Spring>
                <Spring to={{ opacity: 1, scale: 1 }} from={{ opacity: 0, scale: 0.5 }}>
                  {(styles) => (
                    <AnimatedBox style={styles}>
                      <ButtonBase
                        className={classes.closeBasketButton}
                        onClick={() => setActiveBasket(false)}
                      >
                        <KIcon className="fa-solid fa-circle-xmark" />
                      </ButtonBase>
                    </AnimatedBox>
                  )}
                </Spring>
              </>
            )}

            <BasketContainer />
          </Box>
        )}
      </StyledDefaultLayout>
      <DownloadManager />
      <ImportElement />
      <ShareManager />
      <ContactSheets />
      <EditionModal />
      <ThumbnailGeneratorModal />
    </>
  );
};

export default getCustomerOverrideComponent(LayoutOverrideKey.DefaultLayout, DefaultLayout);
